/**
 * Presentational pieces for "Prove Without Reveal".
 *
 * These render the prover state — the auditor's challenge card, the protocol
 * step rail, and the accepting transcript — and emit player intents. They hold
 * no game logic: the pure model in algorithms/proveWithoutReveal.ts decides
 * everything structural.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */
import React from "react";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import type {
  ComplianceChallenge,
  ProverPhase,
  TranscriptEntry,
} from "../../algorithms/proveWithoutReveal.js";

export function proverFlashColor(
  tone: "commit" | "accept" | "stall" | "verified",
): string {
  if (tone === "commit") return "rgba(129,140,248,0.24)";
  if (tone === "accept") return "rgba(52,211,153,0.24)";
  if (tone === "verified") return "rgba(52,211,153,0.3)";
  return "rgba(251,113,133,0.24)";
}

const PHASE_LABEL: Record<ProverPhase, string> = {
  select: "challenge open",
  committed: "committed — awaiting response",
  answered: "answered",
};

export function ChallengeCard({
  challenge,
  index,
  total,
  phase,
  stalled,
}: {
  readonly challenge: ComplianceChallenge;
  readonly index: number;
  readonly total: number;
  readonly phase: ProverPhase;
  readonly stalled: boolean;
}): React.JSX.Element {
  const accent = stalled
    ? panelTokens.violation
    : challenge.witnessHeld
      ? panelTokens.challenge
      : panelTokens.violation;
  return (
    <div
      className="rounded-xl border border-solid p-4"
      style={{
        borderColor: `${accent}66`,
        background: `${accent}12`,
      }}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-mono text-[11px] uppercase text-[var(--zkp-muted)]">
          Auditor challenge {index + 1} / {total}
        </span>
        {PanelSurface.badge(challenge.theorem, accent)}
      </div>
      <div className="mt-2 text-sm font-black text-[var(--zkp-bright)]">
        {challenge.statement}
      </div>
      <div className="mt-1.5 text-[12px] leading-5 text-[var(--zkp-text)]">
        {challenge.auditorNote}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="font-mono text-[11px] text-[var(--zkp-muted)]">
          {PHASE_LABEL[phase]}
        </span>
        {stalled
          ? PanelSurface.badge("acceptance unconstructable", panelTokens.violation)
          : null}
      </div>
    </div>
  );
}

export function ProtocolStepRail({
  phase,
}: {
  readonly phase: ProverPhase;
}): React.JSX.Element {
  const steps: readonly {
    readonly id: ProverPhase | "respond";
    readonly label: string;
    readonly color: string;
  }[] = [
    { id: "select", label: "Open", color: panelTokens.carrier },
    { id: "committed", label: "Commit", color: panelTokens.commit },
    { id: "respond", label: "Respond", color: panelTokens.response },
  ];
  const order: Record<ProverPhase, number> = {
    select: 0,
    committed: 1,
    answered: 2,
  };
  const reached = order[phase];
  return (
    <div className="flex items-center gap-2">
      {steps.map((step, position) => {
        const active = position <= reached;
        return (
          <span
            key={step.id}
            className="rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase"
            style={{
              borderColor: active ? step.color : panelTokens.border,
              color: active ? step.color : panelTokens.muted,
              background: active ? `${step.color}1a` : "transparent",
            }}
          >
            {step.label}
          </span>
        );
      })}
    </div>
  );
}

export function TranscriptList({
  transcript,
}: {
  readonly transcript: readonly TranscriptEntry[];
}): React.JSX.Element {
  if (transcript.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-white/10 p-3 text-[12px] text-[var(--zkp-muted)]">
        No accepting entries yet. Each entry records a commitment and an accept
        response — never a witness edge.
      </div>
    );
  }
  return (
    <ol className="m-0 list-none space-y-2 p-0">
      {transcript.map((entry) => (
        <li
          key={entry.challengeId}
          className="rounded-lg border border-solid p-2.5"
          style={{
            borderColor: `${panelTokens.immune}55`,
            background: `${panelTokens.immune}10`,
          }}
        >
          <div className="flex items-center justify-between gap-2">
            {PanelSurface.badge(entry.theorem, panelTokens.immune)}
            <span className="font-mono text-[10px] uppercase text-[var(--zkp-muted)]">
              accepted
            </span>
          </div>
          <div className="mt-1.5 font-mono text-[11px] text-[var(--zkp-witness)]">
            {entry.commitment} → {entry.response}
          </div>
        </li>
      ))}
    </ol>
  );
}
